import React, { FC } from "react";

import { useTranslation } from "react-i18next";

import { DateFormat, Status } from "@/components/atoms";
import { StatusColorType } from "@/components/atoms/Status/styled";
import { MaxLinesCell, MultiLinesCell, SingleLineCell } from "@/components/molecules";
import { Line } from "@/components/molecules/Table/MultiLinesCell/MultiLinesCell";
import { BrokerState, Claim, Permissions } from "@/shared/domain";
import { useAuth, useNavigation } from "@/shared/hooks";
import { StyledTableCell, StyledTableCellImg, StyledTableRow } from "@/styles/styled";

export type ClaimTableItemProps = {
    claim: Claim;
};

const ClaimTableItem: FC<ClaimTableItemProps> = ({ claim }) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { navToClaimDetail } = useNavigation();

    const canViewDetails = user.hasPermission(Permissions.ViewClaimsDetails, claim.policyholderId);

    const handleClaimDetails = () => {
        return canViewDetails && navToClaimDetail(claim);
    };

    const objectDescriptionLines = [
        {
            key: claim.id,
            line1: claim.objectType.name,
            line2: claim.objectDescription,
        },
    ] as Line[];

    if (claim.victim) {
        objectDescriptionLines.push({
            key: "victim",
            line1: t("Common.Victim"),
            line2: claim.victim,
        });
    }

    return (
        <StyledTableRow hover={canViewDetails} onClick={handleClaimDetails} data-testid={claim.key}>
            <MaxLinesCell
                maxLines={3}
                variant="main"
                highlight
                testId="table-cell-description"
                icon={
                    <StyledTableCellImg
                        style={{ marginRight: "16px" }}
                        src={claim.insurer.logoUrl}
                        alt={`${claim.insurer.name}${claim.insurer.fsma}`}
                    />
                }
            >
                {claim.description}
            </MaxLinesCell>
            <SingleLineCell testId="table-cell-reference" variant="grey" highlight>
                {claim.reference}
            </SingleLineCell>
            <MaxLinesCell testId="table-cell-circumstance" maxLines={3} highlight>
                {claim.circumstance.name}
            </MaxLinesCell>

            <MultiLinesCell
                testId="table-cell-object_description"
                variant1="grey"
                highlight1
                highlight2
                lines={objectDescriptionLines}
            />

            <MaxLinesCell testId="table-cell-damage_event_date" maxLines={3} highlight>
                <DateFormat date={claim.damageEventDate} />
            </MaxLinesCell>
            <StyledTableCell data-testid="table-cell-status">
                <Status
                    color={
                        claim.brokerState.id === BrokerState.ON_GOING ? StatusColorType.Info : StatusColorType.Success
                    }
                    text={claim.brokerState.name}
                />
            </StyledTableCell>
        </StyledTableRow>
    );
};

export default ClaimTableItem;
